import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const CustomerSelector = ({ 
  customers = [], 
  selectedCustomerId = null,
  onSelect,
  onCreateNew,
  placeholder = "Search customers...",
  error = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [createErrors, setCreateErrors] = useState({});
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const selectedCustomer = customers?.find(c => c?.id === selectedCustomerId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredCustomers = customers?.filter(customer => {
    const query = searchQuery?.toLowerCase();
    return (customer?.name?.toLowerCase()?.includes(query) ||
    customer?.email?.toLowerCase()?.includes(query) || customer?.phone?.includes(query));
  });

  const handleSelect = (customer) => {
    onSelect(customer?.id);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCreateCustomer = async () => {
    const errors = {};

    if (!newCustomer?.name?.trim()) {
      errors.name = 'Name is required';
    }
    if (!newCustomer?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(newCustomer?.email)) {
      errors.email = 'Invalid email format';
    }
    if (!newCustomer?.phone?.trim()) {
      errors.phone = 'Phone is required';
    }

    if (Object.keys(errors)?.length > 0) {
      setCreateErrors(errors);
      return;
    }

    try {
      // Call the async onCreateNew and wait for the database-generated customer
      const createdCustomer = await onCreateNew(newCustomer);
      
      // Use the database-generated ID from the created customer
      if (createdCustomer?.id) {
        onSelect(createdCustomer?.id);
      }
      
      setNewCustomer({ name: '', email: '', phone: '' });
      setShowCreateForm(false);
      setIsOpen(false);
      setCreateErrors({});
    } catch (error) {
      console.error('Error creating customer:', error);
      setCreateErrors({ submit: 'Failed to create customer. Please try again.' });
    }
  };

  const handleClear = () => {
    onSelect(null);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-foreground mb-2">
        Customer {error && <span className="text-error">*</span>}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 bg-background border rounded-lg text-left transition-smooth ${
            error 
              ? 'border-error focus:ring-error' :'border-input focus:ring-ring'
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background`}
        >
          <span className={selectedCustomer ? 'text-foreground' : 'text-muted-foreground'}>
            {selectedCustomer ? selectedCustomer?.name : placeholder}
          </span>
          <div className="flex items-center gap-2">
            {selectedCustomer && (
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-muted rounded transition-smooth"
              >
                <Icon name="X" size={16} />
              </button>
            )}
            <Icon 
              name={isOpen ? "ChevronUp" : "ChevronDown"} 
              size={20} 
              className="text-muted-foreground"
            />
          </div>
        </button>

        {error && (
          <p className="mt-1 text-sm text-error">{error}</p>
        )}
      </div>
      {isOpen && (
        <div className="absolute z-dropdown w-full mt-2 bg-popover border border-border rounded-lg shadow-elevation-3 overflow-hidden">
          {!showCreateForm ? (
            <>
              <div className="p-3 border-b border-border">
                <div className="relative">
                  <Icon 
                    name="Search" 
                    size={18} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    placeholder="Search by name, email, or phone..."
                    className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
                    autoFocus
                  />
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto">
                {filteredCustomers?.length > 0 ? (
                  filteredCustomers?.map((customer) => (
                    <button
                      key={customer?.id}
                      type="button"
                      onClick={() => handleSelect(customer)}
                      className="w-full px-4 py-3 text-left hover:bg-muted transition-smooth border-b border-border last:border-b-0"
                    >
                      <div className="font-medium text-foreground">{customer?.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {customer?.email} • {customer?.phone}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-muted-foreground">
                    <Icon name="UserX" size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No customers found</p>
                  </div>
                )}
              </div>

              <div className="p-3 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={() => setShowCreateForm(true)}
                >
                  Create New Customer
                </Button>
              </div>
            </>
          ) : (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading font-semibold text-foreground">New Customer</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setCreateErrors({});
                  }}
                  className="p-1 hover:bg-muted rounded transition-smooth"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <Input
                label="Full Name"
                type="text"
                value={newCustomer?.name}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e?.target?.value }))}
                error={createErrors?.name}
                placeholder="Enter customer name"
                required
              />

              <Input
                label="Email"
                type="email"
                value={newCustomer?.email}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e?.target?.value }))}
                error={createErrors?.email}
                placeholder="customer@example.com"
                required
              />

              <Input
                label="Phone"
                type="tel"
                value={newCustomer?.phone}
                onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e?.target?.value }))}
                error={createErrors?.phone}
                placeholder="+1 (555) 000-0000"
                required
              />

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    setShowCreateForm(false);
                    setCreateErrors({});
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  fullWidth
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleCreateCustomer}
                >
                  Create
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerSelector;